import { z } from 'zod';
import { localizedTextSchema } from './locale';

const base = {
  id: z.string().min(1),
  schemaVersion: z.number().int().positive(),
};

const unit = z.number().min(0).max(1); // normalized 0..1 hotspot coordinate

// ---- structured primitives -------------------------------------------------
export const headingBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('heading'),
    text: localizedTextSchema,
    level: z.union([z.literal(2), z.literal(3)]),
  })
  .strict();

export const paragraphBlockSchema = z
  .object({ ...base, blockType: z.literal('paragraph'), markdown: localizedTextSchema })
  .strict();

export const imageBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('image'),
    src: z.string().min(1),
    alt: localizedTextSchema,
    caption: localizedTextSchema.optional(),
  })
  .strict();

export const calloutBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('callout'),
    variant: z.enum(['info', 'warning', 'danger']),
    markdown: localizedTextSchema,
  })
  .strict();

export const checklistBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('checklist'),
    items: z.array(localizedTextSchema).min(1),
  })
  .strict();

export const keyTakeawayBlockSchema = z
  .object({ ...base, blockType: z.literal('keyTakeaway'), markdown: localizedTextSchema })
  .strict();

// ---- interactive (UNGRADED - engagement + telemetry only) ------------------
export const hotspotImageBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('hotspotImage'),
    src: z.string().min(1),
    hotspots: z
      .array(
        z.object({ id: z.string().min(1), x: unit, y: unit, label: localizedTextSchema }).strict(),
      )
      .min(1),
  })
  .strict();

/**
 * Hotspot scene: same tap-to-learn contract as hotspotImage, but the backdrop
 * is a renderer-owned SVG illustration (like ANIMATION_SCENES - nothing to
 * 404). The scene vocabulary is renderer-owned; adding a scene = renderer +
 * this enum. Hotspot x/y are normalized against the scene's canvas.
 */
export const HOTSPOT_SCENES = ['harnessPoints', 'unconsciousCheck', 'flatFireRisks'] as const;

export const hotspotSceneBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('hotspotScene'),
    scene: z.enum(HOTSPOT_SCENES),
    hotspots: z
      .array(
        z.object({ id: z.string().min(1), x: unit, y: unit, label: localizedTextSchema }).strict(),
      )
      .min(1),
  })
  .strict();

export const orderingBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('ordering'),
    prompt: localizedTextSchema,
    items: z.array(z.object({ id: z.string().min(1), label: localizedTextSchema }).strict()).min(2),
  })
  .strict();

export const knowledgeCheckBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('knowledgeCheck'),
    prompt: localizedTextSchema,
    choices: z
      .array(z.object({ id: z.string().min(1), label: localizedTextSchema }).strict())
      .min(2),
    correctChoiceId: z.string().min(1),
    explanation: localizedTextSchema.optional(),
  })
  .strict()
  .refine((b) => b.choices.some((c) => c.id === b.correctChoiceId), {
    message: 'correctChoiceId must reference one of the choices',
    path: ['correctChoiceId'],
  });

export const videoBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('video'),
    src: z.string().min(1),
    poster: z.string().optional(),
    captions: localizedTextSchema.optional(),
  })
  .strict();

export const accordionBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('accordion'),
    items: z.array(z.object({ q: localizedTextSchema, a: localizedTextSchema }).strict()).min(1),
  })
  .strict();

/**
 * Self-contained animated scenes (SVG + CSS in the renderer - no remote assets).
 * The scene vocabulary is renderer-owned; adding a scene = renderer + this enum.
 */
export const ANIMATION_SCENES = [
  'fireTriangle',
  'extinguisherMatch',
  'cprRhythm',
  'ppeMatch',
  'ladderAngle',
  'lockoutTagout',
  'gasLeak',
  'atmosphereTest',
  'chemicalBottles',
  'liftTechnique',
  'fireWatch',
  'cargoShift',
  'siteInduction',
  'toolCheck',
  'ladderContact',
  'chainsawKickback',
  'asbestosStop',
  'checkInLoop',
  'refrigerantLeak',
  'bitumenTorch',
  'pressureRelief',
] as const;

export type AnimationSceneId = (typeof ANIMATION_SCENES)[number];

/**
 * Per-scene contract shared by content and renderer: how many guided steps the
 * scene draws (1 = ambient loop, no step rail) and its art version. The
 * renderer BUMPS `version` whenever a scene's geometry or in-art microcopy
 * changes - computeVersionHash folds it in so the immutable published snapshot
 * records the redraw without changing qualification validity. A content block's
 * `steps` must match `steps` here (validated below).
 */
export const ANIMATION_SCENE_SPEC = {
  fireTriangle: { steps: 1, version: 2 },
  extinguisherMatch: { steps: 3, version: 2 },
  cprRhythm: { steps: 1, version: 4 },
  ppeMatch: { steps: 3, version: 2 },
  ladderAngle: { steps: 3, version: 4 },
  lockoutTagout: { steps: 5, version: 3 },
  gasLeak: { steps: 3, version: 3 },
  atmosphereTest: { steps: 2, version: 3 },
  chemicalBottles: { steps: 2, version: 2 },
  liftTechnique: { steps: 2, version: 3 },
  fireWatch: { steps: 3, version: 3 },
  cargoShift: { steps: 2, version: 2 },
  siteInduction: { steps: 3, version: 3 },
  toolCheck: { steps: 2, version: 2 },
  ladderContact: { steps: 2, version: 2 },
  chainsawKickback: { steps: 2, version: 2 },
  asbestosStop: { steps: 3, version: 3 },
  checkInLoop: { steps: 3, version: 3 },
  refrigerantLeak: { steps: 2, version: 2 },
  bitumenTorch: { steps: 2, version: 2 },
  pressureRelief: { steps: 2, version: 2 },
} as const satisfies Record<AnimationSceneId, { steps: number; version: number }>;

/** Art version per hotspot illustration - same bump rule as ANIMATION_SCENE_SPEC. */
export const HOTSPOT_SCENE_VERSIONS = {
  harnessPoints: 3,
  unconsciousCheck: 3,
  flatFireRisks: 2,
} as const satisfies Record<(typeof HOTSPOT_SCENES)[number], number>;

/** One guided step: the rail label and the one-sentence instruction read out while it shows. */
export const animationStepSchema = z
  .object({ label: localizedTextSchema, say: localizedTextSchema.optional() })
  .strict();

export const animationBlockSchema = z
  .object({
    ...base,
    blockType: z.literal('animation'),
    scene: z.enum(ANIMATION_SCENES),
    caption: localizedTextSchema.optional(),
    /** Authored per step the scene draws - required (and exactly that many) for multi-step scenes, absent for ambient ones. */
    steps: z.array(animationStepSchema).min(2).max(6).optional(),
  })
  .strict()
  .refine((block) => (block.steps?.length ?? 1) === ANIMATION_SCENE_SPEC[block.scene].steps, {
    path: ['steps'],
    error: (issue) => {
      const block = issue.input as { scene: AnimationSceneId; steps?: unknown[] };
      return `scene "${block.scene}" draws ${ANIMATION_SCENE_SPEC[block.scene].steps} step(s), content authored ${block.steps?.length ?? 0}`;
    },
  });

export const blockSchema = z.discriminatedUnion('blockType', [
  headingBlockSchema,
  paragraphBlockSchema,
  imageBlockSchema,
  calloutBlockSchema,
  checklistBlockSchema,
  keyTakeawayBlockSchema,
  hotspotImageBlockSchema,
  hotspotSceneBlockSchema,
  orderingBlockSchema,
  knowledgeCheckBlockSchema,
  videoBlockSchema,
  accordionBlockSchema,
  animationBlockSchema,
]);

export type HeadingBlock = z.infer<typeof headingBlockSchema>;
export type ParagraphBlock = z.infer<typeof paragraphBlockSchema>;
export type ImageBlock = z.infer<typeof imageBlockSchema>;
export type CalloutBlock = z.infer<typeof calloutBlockSchema>;
export type ChecklistBlock = z.infer<typeof checklistBlockSchema>;
export type KeyTakeawayBlock = z.infer<typeof keyTakeawayBlockSchema>;
export type HotspotImageBlock = z.infer<typeof hotspotImageBlockSchema>;
export type HotspotSceneBlock = z.infer<typeof hotspotSceneBlockSchema>;
export type HotspotScene = HotspotSceneBlock['scene'];
export type OrderingBlock = z.infer<typeof orderingBlockSchema>;
export type KnowledgeCheckBlock = z.infer<typeof knowledgeCheckBlockSchema>;
export type VideoBlock = z.infer<typeof videoBlockSchema>;
export type AccordionBlock = z.infer<typeof accordionBlockSchema>;
export type AnimationBlock = z.infer<typeof animationBlockSchema>;
export type AnimationScene = AnimationBlock['scene'];

export type Block = z.infer<typeof blockSchema>;
export type BlockType = Block['blockType'];

export const BLOCK_TYPES = [
  'heading',
  'paragraph',
  'image',
  'callout',
  'checklist',
  'keyTakeaway',
  'hotspotImage',
  'hotspotScene',
  'ordering',
  'knowledgeCheck',
  'video',
  'accordion',
  'animation',
] as const satisfies readonly BlockType[];
